/**
 * "Dagens pass" at the top of Home (pedagogy spec §6.3.4, S1, §12 D1): up to three steps with a
 * status in words, counts, badges and a time estimate; "Starta passet" or "Fortsätt passet" leads
 * to the runner at `/practice/today`; "Anpassa" opens the small form. The plan is
 * `GET /api/practice/today` and the card count comes from `GET /api/flashcards/decks`.
 *
 * It renders nothing while an exam is running (the live paper stays closed) or when the plan
 * cannot be read: Home still works without it. Empty steps are hidden; with no step at all it
 * says there is nothing planned.
 */
import { Link } from "react-router";
import { useMemo, useState } from "react";
import { MATH_SUPPORT_TOPIC } from "@shared/userPrefs";
import { useTopicsAndChapters } from "@client/api/topics";
import { useAuth } from "@client/app/auth";
import { useLocalizedText } from "@client/components/Localized";
import { Button, Card } from "@client/components/ui";
import { useT } from "@client/i18n";
import { AdjustDialog } from "./AdjustDialog";
import { useTodayData } from "./api";
import { PRIMARY_LINK } from "./linkClass";
import { loadRunner } from "./session";
import { StepList } from "./StepList";
import { buildSteps, hasBegun, isFinished } from "./steps";
import { TodayEvidence, TodayTally } from "./TodayEvidence";
import { TodayExtras } from "./TodayExtras";

/** Grey bars while the plan loads; no live region, so nothing is announced twice. */
function TodayCardSkeleton({ title }: { title: string }) {
  return (
    <Card title={title} className="mb-6" aria-busy="true">
      <div aria-hidden="true" className="space-y-3">
        <div className="h-14 animate-pulse rounded-lg bg-ink-100 dark:bg-ink-800" />
        <div className="h-14 animate-pulse rounded-lg bg-ink-100 dark:bg-ink-800" />
      </div>
    </Card>
  );
}

export function TodayCard() {
  const t = useT();
  const localize = useLocalizedText();
  const topics = useTopicsAndChapters();
  const { settings, skip, plan, mixed, apply } = useTodayData({ questions: false });
  const { user } = useAuth();
  const [adjusting, setAdjusting] = useState(false);
  // Read once: the card lives on Home, and the runner writes the state while it is away.
  const [stored] = useState(() => loadRunner());

  const data = plan.data;
  const runner = stored && data && stored.dayKey === data.dayKey ? stored : null;
  const steps = useMemo(
    () => (data ? buildSteps({ plan: data, mixed: mixed.data, settings, runner }) : []),
    [data, mixed.data, settings, runner],
  );

  if (plan.isError) return null;
  if (!data || mixed.isLoading) return <TodayCardSkeleton title={t("practice.today.title")} />;
  if (data.examActive) return null;

  const topicLabel = (id: string) => {
    const topic = topics.data?.topics.find((candidate) => candidate.id === id);
    return topic ? localize(topic.name) : id;
  };
  const finished = isFinished(steps);

  return (
    <Card
      title={t("practice.today.title")}
      description={t("practice.today.description")}
      className="mb-6"
    >
      {steps.length === 0 ? (
        // The exam's suggestions are something planned too: no "nothing today" beside them.
        data.extras.length === 0 && (
          <p className="text-sm text-ink-700 dark:text-ink-200">{t("practice.today.empty")}</p>
        )
      ) : (
        <StepList steps={steps} topicLabel={topicLabel} />
      )}
      <TodayExtras extras={data.extras} />
      {data.evidence && <TodayTally evidence={data.evidence} />}
      {user?.prefs?.mathConfidence === "unsure" && (
        // "Kom igång" (slice C): unsure about the maths → the tools topic and the answer format.
        <p className="mt-3 text-sm text-ink-700 dark:text-ink-200">
          {t("practice.today.mathSupport")}{" "}
          <Link
            to={`/library/topics#${MATH_SUPPORT_TOPIC}`}
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            {topicLabel(MATH_SUPPORT_TOPIC)}
          </Link>
          {" · "}
          <Link
            to="/hjalp#svar"
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            {t("practice.today.mathSupport.help")}
          </Link>
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {steps.length > 0 && !finished && (
          <Link to="/practice/today" className={PRIMARY_LINK}>
            {hasBegun(steps) ? t("practice.today.continue") : t("practice.today.start")}
          </Link>
        )}
        {finished && (
          <p className="text-sm font-medium text-ink-700 dark:text-ink-200">
            {t("practice.today.finished")}
          </p>
        )}
        <Button variant="secondary" onClick={() => setAdjusting(true)}>
          {t("practice.today.adjust")}
        </Button>
        {steps.length > 0 && !finished && (
          <p className="text-sm text-ink-600 dark:text-ink-400">
            {t("practice.today.stopAnytime")}
          </p>
        )}
      </div>
      {data.evidence && <TodayEvidence evidence={data.evidence} />}
      <AdjustDialog
        open={adjusting}
        onClose={() => setAdjusting(false)}
        settings={settings}
        skip={skip}
        plannedTopicId={data.newTopic?.topicId ?? null}
        plannedLecture={settings.newTopic ? null : (data.newTopic?.lecture ?? null)}
        onSave={apply}
      />
    </Card>
  );
}
